import React from 'react';
import Box from '@mui/system/Box';
import { useStacklineTheme } from '@stackline/ui';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { useLocation, useHistory, useAppSelector } from 'src/utils/Hooks';
import AddEditAlert from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AddEditAlert';
import AlertTables from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertTables';
import AlertTemplatesPage from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/common/AlertTemplatesPage';

export enum AlertsTab {
  MyAlerts = 'myAlerts',
  TeamAlerts = 'teamAlerts'
}

export enum AlertsTabsDisplayName {
  myAlerts = 'My Alerts',
  teamAlerts = 'Team Alerts'
}

export enum AlertsTabsPath {
  myAlerts = '/account/alerts',
  teamAlerts = '/account/alerts/team',
  templateAlert = '/account/alerts/template',
  createAlert = '/account/alerts/create',
  editAlert = '/account/alerts/edit' // this needs to be updated to /account/alerts/edit/:alertId
}

interface AlertsNavigationTabsProps {
  tab: AlertsTab;
}

export const AlertsNavigationTabs = ({ tab }: AlertsNavigationTabsProps) => {
  const history = useHistory();
  const app = useAppSelector((state) => state.app);
  const { queryParams } = app;
  const { searchParams } = queryParams;

  const navigateToPath = (path: string) => {
    history.push(`${path}${searchParams}`);
  };

  return (
    <GenericNavigationTabs
      tab={tab}
      tabs={[
        {
          label: AlertsTabsDisplayName.myAlerts,
          onClick: () => navigateToPath(AlertsTabsPath.myAlerts),
          value: AlertsTab.MyAlerts
        },
        {
          label: AlertsTabsDisplayName.teamAlerts,
          onClick: () => navigateToPath(AlertsTabsPath.teamAlerts),
          value: AlertsTab.TeamAlerts
        }
      ]}
    />
  );
};

const CreateAlertButton = () => {
  const history = useHistory();
  const app = useAppSelector((state) => state.app);
  const { queryParams } = app;
  const { searchParams } = queryParams;

  const handleCreateAlert = () => {
    history.push(`${AlertsTabsPath.templateAlert}${searchParams}`);
  };

  return (
    <SlButton variant="contained" onClick={handleCreateAlert}>
      + Create Alert
    </SlButton>
  );
};

const AlertsTopNav = () => {
  return (
    <Flex justifyContent="space-between" flexDirection="row" alignItems="center">
      <Text variant="h2">Alert Builder</Text>
      <CreateAlertButton />
    </Flex>
  );
};

const AtlasV2AlertsPageLayout: React.FC = () => {
  const theme = useStacklineTheme();
  const location = useLocation();

  if (location.pathname === AlertsTabsPath.templateAlert) {
    return <AlertTemplatesPage />;
  }

  // If we're on the create or edit alert page, render the AddEditAlert component
  const isCreateAlert = location.pathname === AlertsTabsPath.createAlert;
  const isEditAlert = location.pathname === AlertsTabsPath.editAlert;

  if (isCreateAlert || isEditAlert) {
    // AddEditAlert now handles all template logic internally
    return <AddEditAlert />;
  }

  const getCurrentTab = (pathname: string): AlertsTab => {
    if (pathname === AlertsTabsPath.teamAlerts) {
      return AlertsTab.TeamAlerts;
    }
    return AlertsTab.MyAlerts;
  };

  return (
    <Box>
      <AlertsTopNav />
      <Box marginBottom={theme.spacing.lg}>
        <AlertsNavigationTabs tab={getCurrentTab(location.pathname)} />
        <BasePageContainer>
          <AlertTables />
        </BasePageContainer>
      </Box>
    </Box>
  );
};

export default AtlasV2AlertsPageLayout;
